import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Compass, MessagesSquare, Map, UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BG =
  "https://images.unsplash.com/photo-1693648793394-0b76b7eb042e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBmdXR1cmlzdGljJTIwcHJlbWl1bSUyMHRleHR1cmV8ZW58MHx8fHwxNzgyMTg2MjQ3fDA&ixlib=rb-4.1.0&q=85";

const steps = [
  { icon: MessagesSquare, t: "It interviews you", d: "An adaptive conversation about what you like, what you're naturally good at, what you've built, and your real-life situation." },
  { icon: UserRound, t: "You review the profile", d: "Loop shows you exactly what it learned. Edit anything. Nothing is assumed about you." },
  { icon: Compass, t: "Possibilities appear", d: "A career, a course, an apprenticeship, a hobby, a project, a place to live, a community, a skill, a way to make money — plus wildcards." },
  { icon: Map, t: "You pick. It maps.", d: "A phased roadmap with skills, projects, resources and a weekly checklist you can actually finish." },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <img src={BG} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-[0.28]" />
      <div className="absolute inset-0 bg-background/70" aria-hidden="true" />
      <div className="relative grid-lines min-h-screen">
        <header className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center">
          <div className="flex items-center gap-2.5">
            <span className="h-2 w-2 rounded-full bg-primary shadow-[0_0_14px_hsl(187_72%_58%)]" />
            <span className="font-display text-lg tracking-tight">loop</span>
          </div>
          <button
            onClick={signIn}
            data-testid="nav-signin-button"
            className="ml-auto text-sm px-5 py-2 rounded-full border border-white/12 text-white hover:bg-white/10 transition-colors duration-200"
          >
            Sign in
          </button>
        </header>

        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 rise">
            <p className="overline">Personal career command centre</p>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight">
              An AI that gets to know you
              <span className="block text-primary mt-3">before it tells you anything.</span>
            </h1>
            <p className="mt-8 text-base text-muted-foreground max-w-xl leading-relaxed">
              Loop interviews you the way a great mentor would — about your interests, natural
              strengths, projects and goals. Then it shows you possibilities you'd never have
              thought to search for. You stay in control of every step.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                onClick={signIn}
                data-testid="google-signin-button"
                className="group flex items-center gap-3 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
              >
                Continue with Google
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
              </button>
              <span className="text-sm text-muted-foreground">Takes about 10 minutes.</span>
            </div>
          </div>

          <div className="lg:col-span-5 lg:pt-16 space-y-3">
            {steps.map(({ icon: Icon, t, d }, i) => (
              <div
                key={t}
                className="panel p-6 rise hover:bg-white/[0.045] transition-colors duration-200"
                style={{ animationDelay: `${120 + i * 90}ms` }}
              >
                <div className="flex items-start gap-4">
                  <Icon className="h-5 w-5 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="font-display text-base">{t}</p>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{d}</p>
                  </div>
                  <span className="ml-auto overline">0{i + 1}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
